import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/app/context/AuthContext';
import { Logo } from '@/app/components/Logo';
import { trySendLicenseToExtension, isExtensionSource } from '@/lib/extensionBridge';

/**
 * Google OAuth Callback Page
 * Handles the redirect from Google after user authorization
 * Exchanges the authorization code for user info via backend
 */
export function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  
  // Use ref to ensure we only process the callback once
  const hasProcessed = useRef(false);

  // Memoize the callback processing to avoid dependency issues
  const processCallback = useCallback(async () => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Check for errors from Google
    if (errorParam) {
      setError(errorDescription || 'Google authorization was denied');
      setIsProcessing(false);
      return;
    }

    // Validate state parameter (CSRF protection)
    const storedState = localStorage.getItem('google_oauth_state');
    
    // Debug logging
    console.log('Google OAuth state validation:', {
      urlState: state,
      storedState: storedState,
      match: state === storedState
    });
    
    if (!state || state !== storedState) {
      console.error('State mismatch:', { urlState: state, storedState: storedState });
      setError('Invalid state parameter. Please try again.');
      setIsProcessing(false);
      return;
    }

    // Clear stored state immediately after validation
    localStorage.removeItem('google_oauth_state');

    if (!code) {
      setError('No authorization code received from Google');
      setIsProcessing(false);
      return;
    }

    try {
      // Check if user was trying to checkout a paid plan BEFORE calling login
      // This allows us to pass intent='paid' to prevent free license creation
      const checkoutPlan = sessionStorage.getItem('post_auth_checkout_plan');
      const isPaidCheckout = checkoutPlan && checkoutPlan.toLowerCase() !== 'free';
      
      // Exchange code for user info via backend
      // Pass intent='paid' if user is checking out a paid plan to prevent free license creation
      const result = await loginWithGoogle(code, isPaidCheckout ? 'paid' : undefined);
      const billingInterval = sessionStorage.getItem('post_auth_billing_interval') || 'monthly';
      const includedPackageId = sessionStorage.getItem('post_auth_checkout_included_package_id') || null;
      
      if (checkoutPlan && isPaidCheckout) {
        sessionStorage.removeItem('post_auth_checkout_plan');
        sessionStorage.removeItem('post_auth_billing_interval');
        sessionStorage.removeItem('post_auth_checkout_included_package_id');
        
        // Map plan names to API format
        const planMap: Record<string, string> = {
          'Starter': 'starter',
          'Pro': 'pro',
          'Pro+': 'pro_plus',
          'Ultimate': 'ultimate',
          'Enterprise': 'enterprise'
        };
        const apiPlanName = planMap[checkoutPlan] || checkoutPlan.toLowerCase();
        
        // Navigate to embedded checkout page instead of redirecting to Stripe hosted checkout
        const checkoutParams = new URLSearchParams({
          plan: apiPlanName,
          billing: billingInterval,
          type: 'new_subscription',
          ...(includedPackageId ? { included_package_id: includedPackageId } : {}),
        });
        navigate(`/checkout?${checkoutParams.toString()}`, { replace: true });
        return;
      }

      // Handle extension source or normal redirect
      if (isExtensionSource() && !result.user.is_new_user && result.license?.license_key) {
        // Existing user from extension: send license key directly to extension
        const sent = await trySendLicenseToExtension(
          result.license.license_key,
          result.license.plan || 'free',
        );
        navigate(
          `/success?licenseKey=${encodeURIComponent(result.license.license_key)}&extensionSent=${sent}`,
          { replace: true },
        );
      } else if (isExtensionSource() && result.user.is_new_user) {
        // New user from extension: redirect to pricing to choose a plan
        navigate('/pricing', { replace: true });
      } else if (result.user.is_new_user) {
        navigate(`/success?licenseKey=${encodeURIComponent(result.license.license_key)}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      console.error('Google callback error:', err);
      setError(err.message || 'Failed to complete Google sign-in');
      setIsProcessing(false);
    }
  }, [searchParams, loginWithGoogle, navigate]);

  useEffect(() => {
    processCallback();
  }, [processCallback]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-[#0A1628] dark:to-[#0F172A] flex items-center justify-center">
      <div className="text-center p-8">
        <div className="flex justify-center mb-6">
          <Logo size="lg" />
        </div>
        
        {isProcessing ? (
          <>
            <div className="w-12 h-12 border-4 border-[#4285F4] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Completing Sign-In
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we verify your Google account...
            </p>
          </>
        ) : error ? (
          <>
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Sign-In Failed
            </h2>
            <p className="text-red-600 dark:text-red-400 mb-6">
              {error}
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-[#1E3A8A] text-white rounded-lg hover:bg-[#1E3A8A]/90 transition-all"
            >
              Return to Home
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
